/**
 * UranIA PA — i18n bootstrap (US-009).
 *
 * `it-IT` primario (analisi §16.1, ADR-016 italian-first); `en-EU` fallback
 * per auditor CE. Termini di dominio NON sono tradotti: i bundle EN
 * preservano volutamente Base Giuridica, GBER, FESR, SORI, DGR ecc.
 *
 * Pattern: usiamo il singleton globale `i18next` con `initReactI18next` —
 * così tutte le componenti che invocano `useTranslation()` lo trovano via
 * React Context interno del plugin, senza dover wrappare manualmente con
 * `<I18nextProvider>` (i18next 23+ + react-i18next 15+).
 *
 * Init sincrono: tutte le risorse sono già in memoria, niente backend
 * remoto. `.init()` restituisce una Promise ma è già `isInitialized=true`
 * non appena la chiamata ritorna, perché non c'è backend asincrono. Niente
 * top-level await (no async chunk al boot).
 */

import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import enBundle from './locales/en.json';
import itBundle from './locales/it.json';

export const DEFAULT_LANGUAGE = 'it-IT' as const;
export const FALLBACK_LANGUAGE = 'en-EU' as const;

export const SUPPORTED_LANGUAGES = [DEFAULT_LANGUAGE, FALLBACK_LANGUAGE] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const LANGUAGE_STORAGE_KEY = 'urania.lang';

/**
 * Risolve la lingua con cui inizializzare i18next.
 *
 * Policy (ADR-016 italian-first, §16.1):
 *   1. Preferenza utente persistita su `localStorage` (chiave
 *      `urania.lang`) → vince se è uno dei `SUPPORTED_LANGUAGES`.
 *   2. Altrimenti default a `it-IT`. NON si ricade su `navigator.language`
 *      perché l'utenza target è PA italiana: chi vuole inglese (auditor CE)
 *      lo seleziona esplicitamente da un toggle UI futuro che scrive su
 *      `urania.lang`.
 *
 * Questo allinea letteralmente l'AC US-009 #3 "it-IT primario, en-EU
 * fallback": fallback = lingua usata se manca una chiave nel bundle
 * primario, non come default per browser non-italiani.
 */
function resolveInitialLanguage(): SupportedLanguage {
  if (typeof window !== 'undefined') {
    try {
      const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
      if (stored === DEFAULT_LANGUAGE || stored === FALLBACK_LANGUAGE) {
        return stored;
      }
    } catch {
      // Accesso a localStorage può sollevare SecurityError in iframe
      // sandboxati. Fallback al default.
    }
  }
  return DEFAULT_LANGUAGE;
}

export async function initI18n(): Promise<void> {
  if (i18n.isInitialized) {
    return;
  }
  const lng = resolveInitialLanguage();
  await i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources: {
        'it-IT': { translation: itBundle },
        'en-EU': { translation: enBundle },
      },
      lng,
      fallbackLng: FALLBACK_LANGUAGE,
      supportedLngs: [...SUPPORTED_LANGUAGES],
      interpolation: {
        escapeValue: false,
      },
      detection: {
        // `lng` esplicito ha già scelto: il detector qui serve solo a
        // ricordare la scelta utente futura nella stessa chiave
        // localStorage.
        order: ['localStorage'],
        lookupLocalStorage: LANGUAGE_STORAGE_KEY,
        caches: ['localStorage'],
      },
      react: {
        // `Suspense` non è necessario: tutte le risorse sono in memoria e
        // il mount avviene dopo `await initI18n()` (vedi `main.tsx`).
        useSuspense: false,
      },
      returnNull: false,
    });
}

export { i18n as i18nInstance };
